import { HOME_COUNTRY_CODE } from '@carcheck/shared';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useCities, useCountries, type CityOption } from '../../api/geo';

/**
 * «Страна» + «Город» from the public dictionary (AUT-761), shared by the
 * company form and the list filters.
 *
 *   - `form`   — the country always has a value (empty = Belarus, the
 *                server's rule); outside Belarus the list ends with
 *                «Другой город», which opens a text field for the own name.
 *   - `filter` — both start at «Любая / Любой»; the city waits for a country.
 *
 * Changing the country clears the city. The city value is the dictionary
 * key (the wire form), or the typed text when `custom`.
 */
export interface PlaceValue {
  cityCountry: string;
  city: string;
  cityCustom: boolean;
}

export interface PlaceSelectProps {
  mode: 'form' | 'filter';
  value: PlaceValue;
  onChange: (next: PlaceValue) => void;
  disabled?: boolean;
  /** Prefix of the `data-testid`s: `<prefix>-country`, `-city`, `-custom`. */
  testId: string;
  /** Max length of an own city name. */
  customMax?: number;
  sx?: object;
}

const OTHER_KEY = '__other__';
const ANY = '';

interface CountryFieldProps {
  isForm: boolean;
  value: string;
  disabled: boolean;
  testId: string;
  onPick: (code: string) => void;
}

const CountryField = ({
  isForm,
  value,
  disabled,
  testId,
  onPick,
}: CountryFieldProps): ReactElement => {
  const { t } = useTranslation();
  const countries = useCountries();
  return (
    <TextField
      select
      size="small"
      fullWidth={isForm}
      label={t('place.country')}
      value={(countries.data ?? []).some((c) => c.code === value) ? value : ANY}
      disabled={disabled}
      onChange={(e) => onPick(e.target.value)}
      slotProps={{
        inputLabel: { shrink: true },
        select: { displayEmpty: true },
        htmlInput: { 'data-testid': `${testId}-country` },
      }}
      sx={{ minWidth: 160 }}
    >
      {isForm ? null : <MenuItem value={ANY}>{t('place.anyCountry')}</MenuItem>}
      {(countries.data ?? []).map((c) => (
        <MenuItem key={c.code} value={c.code}>
          {c.name}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const PlaceSelect = ({
  mode,
  value,
  onChange,
  disabled = false,
  testId,
  customMax,
  sx,
}: PlaceSelectProps): ReactElement => {
  const { t } = useTranslation();
  const isForm = mode === 'form';
  const country = value.cityCountry || (isForm ? HOME_COUNTRY_CODE : ANY);
  const cities = useCities(country);
  const allowOther = isForm && country !== HOME_COUNTRY_CODE;

  const options = useMemo<CityOption[]>(() => {
    const list = cities.data ?? [];
    return allowOther ? [...list, { key: OTHER_KEY, name: t('place.otherCity') }] : list;
  }, [cities.data, allowOther, t]);

  const selected: CityOption | null = value.cityCustom
    ? (options.find((o) => o.key === OTHER_KEY) ?? null)
    : (options.find((o) => o.key === value.city || o.name === value.city) ?? null);

  const pickCountry = (code: string): void =>
    onChange({ cityCountry: code, city: '', cityCustom: false });

  const pickCity = (option: CityOption | null): void => {
    if (option === null) return onChange({ cityCountry: country, city: '', cityCustom: false });
    if (option.key === OTHER_KEY)
      return onChange({ cityCountry: country, city: '', cityCustom: true });
    return onChange({ cityCountry: country, city: option.key, cityCustom: false });
  };

  return (
    <Box sx={{ display: 'contents', ...sx }}>
      <CountryField
        isForm={isForm}
        value={country}
        disabled={disabled}
        testId={testId}
        onPick={pickCountry}
      />
      <Autocomplete<CityOption>
        size="small"
        fullWidth={isForm}
        options={options}
        value={selected}
        disabled={disabled || country === ANY}
        loading={cities.isLoading}
        getOptionLabel={(o) => o.name}
        isOptionEqualToValue={(a, b) => a.key === b.key}
        onChange={(_e, next) => pickCity(next)}
        noOptionsText={t('place.noCities')}
        sx={{ minWidth: 180 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('place.city')}
            inputProps={{ ...params.inputProps, 'data-testid': `${testId}-city` }}
          />
        )}
      />
      {value.cityCustom ? (
        <TextField
          size="small"
          fullWidth
          label={t('place.customCity')}
          value={value.city}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, cityCountry: country, city: e.target.value })}
          slotProps={{
            htmlInput: {
              'data-testid': `${testId}-custom`,
              ...(customMax ? { maxLength: customMax } : {}),
            },
          }}
        />
      ) : null}
    </Box>
  );
};
